import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { EmployeeService } from "../../services/employee.service";
import { ToastrService } from "ngx-toastr";
import { Urls } from "src/app/config/urls.config";
import { AuthService } from "src/app/services/auth.service";
import { ErrorHandlerService } from "src/app/shared/error-handler.service";
import { LoggerService } from "../../../../shared/logger.service";
import Swal from "sweetalert2";

interface RoleResponse {
  name: string;
}

@Component({
  selector: "app-employee-list",
  templateUrl: "./employee-list.component.html",
  styleUrls: ["./employee-list.component.css"],
})
export class EmployeeListComponent implements OnInit {
  employeeHeaders = ["Id", "Foto", "Nome", "Cargo", "Permissão"];
  employeeData = [];

  userRoles: RoleResponse[] = [];
  isAdmin: boolean = false;
  isSupervisor: boolean = false;
  isManager: boolean = false;
  isUser: boolean = false;

  userForm: FormGroup;
  loading: boolean = true; // Estado de carregamento
  deleting: boolean = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private employeeService: EmployeeService,
    public router: Router,
    private toast: ToastrService,
    private errorHandlerService: ErrorHandlerService,
    private logger: LoggerService
  ) {
    this.userForm = this.fb.group({
      name: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.loadEmployeeData();
    this.getUserRoles();
  }

  private getUserRoles(): void {
    this.authService.getUserRoles().forEach((role) => {
      switch (role.name) {
        case "ROLE_ADMIN":
          this.isAdmin = true;
          break;
        case "ROLE_MANAGER":
          this.isManager = true;
          break;
        case "ROLE_SUPERVISOR":
          this.isSupervisor = true;
          break;
        case "ROLE_USER":
          this.isUser = true;
          break;
        default:
          this.isUser = true;
      }
    });
  }

  private loadEmployeeData(): void {
    this.employeeService.findAllByContractType("CLT").subscribe({
      next: (employees) => {
        if (employees != null) {
          employees.forEach((response) => {
            response.picture = response.picture == null ? Urls.getDefaultPictureS3() : response.picture;
            let accessType =
              response.accessType == "Manager"
                ? "Gerente"
                : response.accessType == "User"
                ? "Usuário"
                : response.accessType == "Supervisor"
                ? "Supervisor"
                : "";
            const employee = [response.id, response.picture, response.name, response.responsibilityName, accessType];
            this.employeeData.push(employee);
          });
        }
      },
      error: (error) => this.errorHandlerService.handle(error, "Não foi possível buscar os funcionários"),
      complete: () => (this.loading = false),
    });
  }

  onEdit(employee: any) {
    const id = employee[0];
    this.router.navigate(["/employees/edit/", id]);
  }

  public onDelete(row: any): void {
    const id = row[0];
    const name = row[2];
    Swal.fire({
      title: "Desativar Funcionário",
      html: `
        <div class="delete-dialog-content">
          <div class="delete-icon-wrapper">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#f44336"/>
            </svg>
          </div>
          <p class="delete-dialog-title">Tem certeza que deseja desativar este funcionário?</p>
          <div class="delete-dialog-employee-info">
            <strong>${name}</strong>
          </div>
          <p class="delete-dialog-warning">Esta ação não pode ser desfeita. O funcionário será desativado e não poderá mais acessar o sistema.</p>
        </div>
      `,
      icon: "warning",
      iconColor: "#f44336",
      showCancelButton: true,
      confirmButtonText: "Sim, desativar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#6c757d",
      buttonsStyling: true,
      reverseButtons: true,
      focusCancel: true,
      customClass: {
        popup: "delete-dialog-popup",
        title: "delete-dialog-swal-title",
        htmlContainer: "delete-dialog-html-container",
        confirmButton: "delete-dialog-confirm-button",
        cancelButton: "delete-dialog-cancel-button",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleting = true;
        this.employeeService.delete(id).subscribe({
          next: () => {
            Swal.fire({
              title: "Desativado!",
              text: `O funcionário ${name} foi desativado com sucesso.`,
              icon: "success",
              confirmButtonColor: "#4caf50",
              confirmButtonText: "OK",
              timer: 3000,
              timerProgressBar: true,
            });
            this.deleting = false;
            window.location.reload();
          },
          error: (error) => {
            this.deleting = false;
            Swal.fire({
              title: "Erro!",
              text: "Não foi possível desativar o funcionário. Tente novamente.",
              icon: "error",
              confirmButtonColor: "#f44336",
              confirmButtonText: "OK",
            });
            this.errorHandlerService.handle(error, "Erro ao deletar funcionário(a) selecionado(a).");
          },
        });
      }
    });
  }

  onView(row: any) {
    this.logger.debug("Viewing employee", row);
  }
}
